import * as angular from 'angular';
import { BehaviorSubject } from 'rxjs';

import { ModalTheme, ModalType } from '@mode/shared/ui-angularjs';

export class FormToggleConfirmController {
  private checked: boolean;
  private inputName: string;
  private isNumber: boolean;
  private isStatic: boolean;
  private modalType: ModalType;
  private modalTheme: ModalTheme;
  private confirmOnUncheck: boolean;
  private confirmOnCheck: boolean;
  private onToggle: ($args: { $checked: boolean }) => void;
  private uncheckPromptIsOpen$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private checkPromptIsOpen$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private checked$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor() {
    this.checked$.subscribe((checked) => {
      this.checked = checked;
    });
  }

  $onInit() {
    this.setChecked(this.checked, { silent: true });
  }

  $onChanges(changed: angular.IOnChangesObject) {
    if (changed.checked && this.isStatic) {
      this.checked$.next(changed.checked.currentValue);
    }
  }

  handleConfirm() {
    if (this.isChecked()) {
      if (this.confirmOnUncheck) {
        this.setChecked(false);
      }
    } else {
      if (this.confirmOnCheck) {
        this.setChecked(true);
      }
    }
    this.closePrompt();
  }

  handleClose() {
    this.closePrompt();
  }

  handleClicked() {
    if (this.isChecked()) {
      if (this.confirmOnUncheck) {
        this.openUncheckPrompt();
      } else {
        this.setChecked(false);
      }
    } else {
      if (this.confirmOnCheck) {
        this.openCheckPrompt();
      } else {
        this.setChecked(true);
      }
    }
  }

  openUncheckPrompt(): void {
    this.closePrompt();
    this.uncheckPromptIsOpen$.next(true);
  }

  openCheckPrompt(): void {
    this.closePrompt();
    this.checkPromptIsOpen$.next(true);
  }

  closePrompt(): void {
    this.uncheckPromptIsOpen$.next(false);
    this.checkPromptIsOpen$.next(false);
  }

  getValue(): boolean | number {
    if (this.isNumber) {
      return this.isChecked() ? 1 : 0;
    }

    return this.isChecked();
  }

  getName(): string {
    return this.inputName;
  }

  setChecked(state: boolean, { silent = false } = {}): void {
    if (!this.isStatic) {
      this.checked$.next(state);
    }

    if (this.onToggle && !silent) {
      if (this.isStatic) {
        this.onToggle({ $checked: state });
      } else {
        this.onToggle({ $checked: this.isChecked() });
      }
    }
  }

  isChecked(): boolean {
    return this.checked$.getValue();
  }
}

export const FormToggleConfirmComponent: angular.IComponentOptions = {
  controller: FormToggleConfirmController,
  controllerAs: 'ctrl',
  bindings: {
    checked: '<',
    checkConfirmContent: '<',
    checkConfirmTitle: '<',
    checkConfirmButtonConfirm: '@',
    checkConfirmButtonCancel: '@',
    uncheckConfirmContent: '<',
    uncheckConfirmTitle: '<',
    uncheckConfirmButtonConfirm: '@',
    uncheckConfirmButtonCancel: '@',
    confirmOnCheck: '<',
    confirmOnUncheck: '<',
    desc: '@',
    inputName: '@',
    isNumber: '<',
    isStatic: '<',
    label: '@',
    modalTheme: '@',
    modalType: '@',
    onToggle: '&',
  },
  template: `
    <div class="form-toggle-confirm">
      <span class="form-toggle checkbox-lg">
        <input
          type="hidden"
          name="{{ctrl.getName()}}"
          value="{{ctrl.getValue()}}"
        />

        <input
          type="checkbox"
          ng-checked="ctrl.checked$ | async:this"
        />

        <label>
          <span class="toggle" ng-click="ctrl.handleClicked()">
            <i class="icon-success-check-inverted checkmark"></i>
            <span class="switch"></span>
          </span>

          <div class="toggle-content">
            <p class="toggle-label">{{ ctrl.label }}</p>
            <p class="toggle-desc">{{ ctrl.desc }}</p>
          </div>
        </label>
      </span>

      <modal-dialog
        confirm-button-cancel="{{ctrl.uncheckConfirmButtonCancel}}"
        confirm-button-confirm="{{ctrl.uncheckConfirmButtonConfirm}}"
        content="ctrl.uncheckConfirmContent"
        modal-type="{{ctrl.modalType}}"
        modal-theme="{{ctrl.modalTheme}}"
        on-close="ctrl.handleClose()"
        on-confirm="ctrl.handleConfirm()"
        open="ctrl.uncheckPromptIsOpen$ | async:this"
        title="ctrl.uncheckConfirmTitle"
      />

      <modal-dialog
        confirm-button-cancel="{{ctrl.checkConfirmButtonCancel}}"
        confirm-button-confirm="{{ctrl.checkConfirmButtonConfirm}}"
        content="ctrl.checkConfirmContent"
        modal-type="{{ctrl.modalType}}}"
        modal-theme="{{ctrl.modalTheme}}}"
        on-close="ctrl.handleClose()"
        on-confirm="ctrl.handleConfirm()"
        open="ctrl.checkPromptIsOpen$ | async:this"
        title="ctrl.checkConfirmTitle"
      />
    </div>
  `,
};
